import { useCallback, useEffect, useRef, useState } from 'react';
import { SchreibtAls } from '@/components/ui/SchreibtAls';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { X, AlertCircle, Gift } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/lib/toast';
import { useCreateBestellung } from '@/hooks/useCreateBestellung';
import { ApiError } from '@/lib/api';
import { userLabel } from '@/lib/format';
import { C } from '@/theme';
import { BestellwegPicker } from './BestellwegPicker';
import { ZustellwegPicker } from './ZustellwegPicker';
import { KundenSearch } from './KundenSearch';
import { NeuerKundeForm } from './NeuerKundeForm';
import { IsbnInput } from './IsbnInput';
import { ScanOverlay } from '@/components/dashboard/ScanOverlay';
import { scanDeuten } from '@/lib/scan';
import { useScanHosts } from '@/hooks/useScanHosts';
import { FreitextInput } from './FreitextInput';
import { PositionCard } from './PositionCard';
import { KundenwunschBereich } from './KundenwunschBereich';
import { RechnungsadresseSection } from './RechnungsadresseSection';
import {
  formErrors,
  istFormularLeer,
  neuerKundeToWire,
  useBestellungReducer
} from './useBestellungReducer';
import { useDraft } from '@/hooks/useDraft';
import { DraftHinweis } from '@/components/ui/DraftHinweis';
import { istUebungsModus } from '@/onboarding/uebungsModus';
import type { CreateBestellungRequest } from '@/types/bestellung';
import { faengtAussenklickAb } from '@/onboarding/tourSchutz';
import { useVersandarten, CODE_ABHOLUNG, CODE_VERSAND_SAMMEL } from '@/hooks/useVersandarten';
import { FOKUS_RING } from '@/lib/fokus';
import { melde } from '@/lib/nutzung';

type Props = {
  open: boolean;
  onClose: () => void;
  /**
   * Nach dem Speichern: zur neuen Bestellung, und zwar so, dass sie den
   * Verlaufsschritt des Fensters ERSETZT (lib/neueBestellung.ts). Fehlt
   * er, schließt das Fenster und springt wie bisher.
   */
  onGespeichert?: (bestellnr: string) => void;
};

function SektionTitel({ titel, fehler }: { titel: string; fehler?: string }) {
  return (
    <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
      <h3 className={`text-sm font-medium ${fehler ? 'text-coral' : 'text-ink'}`}>{titel}</h3>
      {fehler && (
        <span className="text-xs text-coral" role="alert">
          {fehler}
        </span>
      )}
    </div>
  );
}

export function NewBestellungSheet({ open, onClose, onGespeichert }: Props) {
  const auth = useAuth();
  const toast = useToast();
  const scanHosts = useScanHosts();
  const navigate = useNavigate();
  const create = useCreateBestellung();
  const [state, dispatch] = useBestellungReducer();
  // Wird beim ersten Speicher-Versuch gesetzt und schaltet die
  // Feldmarkierungen scharf — vorher soll ein leeres Formular nicht
  // sofort rot leuchten (B4).
  const [versucht, setVersucht] = useState(false);
  // E6: Der Scanner im Erfassungs-Formular. Getrennt vom Suchfeld-Scanner
  // im Dashboard, weil er etwas anderes tut: dort wird gesucht, hier wird
  // die ISBN ins Feld geschrieben.
  const [scanOffen, setScanOffen] = useState(false);
  // Solange ein ISBN-Treffer zur Bestaetigung ansteht, gehoert Esc dem
  // Treffer und nicht dem Sheet (B50). Ref statt State: der Wert wird nur
  // im Event-Handler gelesen, ein Re-Render braucht es dafuer nicht.
  const isbnReviewOffen = useRef(false);
  const meldeReviewOffen = useCallback((offen: boolean) => {
    isbnReviewOffen.current = offen;
  }, []);

  // Gebraucht, um die Weiche in eine versandart_id aufzuloesen. Die
  // Abfrage laeuft ohnehin fuer den Picker; hier ist es derselbe
  // Cache-Eintrag, kein zweiter Zugriff.
  const versandarten = useVersandarten();

  const fehler = formErrors(state);
  const hatFehler = Object.keys(fehler).length > 0;

  // Entwurf sichern, solange das Sheet offen ist (S-1).
  const draft = useDraft({
    scope: 'neue-bestellung',
    userId: auth.data?.user.id ?? null,
    wert: state,
    aktiv: open,
    istLeer: istFormularLeer
  });

  // Beim Schliessen Reset, damit das Formular beim naechsten Oeffnen
  // sauber startet (kein Draft).
  useEffect(() => {
    if (!open) {
      dispatch({ type: 'RESET' });
      setVersucht(false);
    }
  }, [open, dispatch]);

  // Nutzungsmessung: Dauer einer Erfassung, vom Öffnen bis zum Speichern.
  const geoeffnetAm = useRef<number | null>(null);
  useEffect(() => {
    geoeffnetAm.current = open ? performance.now() : null;
  }, [open]);

  function trim(s: string): string | null {
    return s.trim() === '' ? null : s.trim();
  }

  /**
   * Loest die Weiche in die versandart_id auf, die das Backend schreibt.
   *
   * Abholung -> der Code `abholung`.
   * Versand  -> die gewaehlte Praezisierung, sonst der Sammelbegriff.
   *
   * Gesucht wird ueber den CODE, nicht ueber die Bezeichnung: die
   * Bezeichnung pflegt der Admin, der Code ist der Schluessel. Fehlt der
   * Eintrag in der Liste — eine stillgelegte Abholung etwa —, geht `null`
   * raus statt einer geratenen ID. Das Backend nimmt `null` an; eine
   * falsche Versandart waere schlimmer als keine.
   */
  function versandartFuerWeg(): number | null {
    const items = versandarten.data?.items ?? [];
    if (state.zustellweg === 'abholung') {
      return items.find((v) => v.code === CODE_ABHOLUNG)?.id ?? null;
    }
    if (state.zustellweg === 'versand') {
      if (state.versandartId !== null) return state.versandartId;
      return items.find((v) => v.code === CODE_VERSAND_SAMMEL)?.id ?? null;
    }
    return null;
  }

  function buildRequest(): CreateBestellungRequest {
    return {
      bestellweg_id: state.bestellwegId!,
      // Im SELBEN Aufruf, nicht als zweiter Schritt: scheiterte der,
      // stand die Bestellung ohne Versandart da und ihre Sendung liess
      // sich nicht mehr buchen (A7.2).
      versandart_id: versandartFuerWeg(),
      kunde_id: state.kundeId,
      neuer_kunde: state.neuerKunde ? neuerKundeToWire(state.neuerKunde) : null,
      ist_geschenk: state.istGeschenk,
      rechnung_abweichend: state.rechnungAbweichend,
      rechnung_anrede: state.rechnungAbweichend ? trim(state.rechnungAdresse.anrede) : null,
      rechnung_strasse: state.rechnungAbweichend ? trim(state.rechnungAdresse.strasse) : null,
      rechnung_plz: state.rechnungAbweichend ? trim(state.rechnungAdresse.plz) : null,
      rechnung_ort: state.rechnungAbweichend ? trim(state.rechnungAdresse.ort) : null,
      rechnung_land: state.rechnungAbweichend ? trim(state.rechnungAdresse.land) : null,
      shopify_order_id: null,
      shopify_order_number: null,
      notiz: trim(state.notiz),
      // A56 (#135): der Wunsch der Kundin. Zwei Angaben, die auf jeder
      // Karteikarte standen und im Tracking fehlten.
      lieferwunsch: state.lieferwunsch,
      nachricht_wunsch: state.nachrichtWunsch,
      abholdatum_zugesagt: state.abholdatum === '' ? null : state.abholdatum,
      // Waehrend der Einfuehrungstour entsteht hier eine Uebung, keine
      // echte Bestellung — der neue Mitarbeiter klickt dieselben Knoepfe
      // wie im Alltag, das Ergebnis sieht aber nur er und verschwindet
      // am Ende wieder.
      ist_uebung: istUebungsModus(),
      positionen: state.positionen.map(({ _uid: _, ...rest }) => rest)
    };
  }

  async function speichern(): Promise<void> {
    // Kein stummes Nichtstun: fehlende Angaben sichtbar machen (B4).
    if (hatFehler) {
      setVersucht(true);
      toast('error', 'Bitte die markierten Angaben ergänzen.');
      return;
    }
    try {
      const res = await create.mutateAsync(buildRequest());
      if (geoeffnetAm.current !== null) {
        melde('erfassung_dauer', { ort: 'neue_bestellung', wert: performance.now() - geoeffnetAm.current });
      }
      // Erst nach bestaetigtem Erfolg wegwerfen (S-1).
      draft.loeschen();
      toast('success', `Bestellung ${res.bestellung.bestellnr} angelegt.`);
      // Direkt zur neuen Bestellung springen (B7).
      if (onGespeichert) {
        onGespeichert(res.bestellung.bestellnr);
      } else {
        onClose();
        void navigate({
          to: '/bestellungen/$bestellnr',
          params: { bestellnr: res.bestellung.bestellnr }
        });
      }
    } catch (err) {
      if (err instanceof ApiError) {
        toast('error', `Speichern fehlgeschlagen: ${err.code}`);
      } else {
        toast('error', 'Speichern fehlgeschlagen.');
      }
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/30" />
        <Dialog.Content
          // Tour-Sprechblase UND Scan-Fenster haengen am <body>: ohne
          // diese Zeile gilt ein Klick dort als Klick daneben und schliesst
          // den Dialog. Beim Scanner hiess das, dass sein eigener
          // "Abbrechen"-Knopf das ganze Formular mitnahm (A5.1).
          onPointerDownOutside={faengtAussenklickAb}
          onInteractOutside={faengtAussenklickAb}
          className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[640px] flex-col bg-cream shadow-xl"
          aria-describedby={undefined}
          data-tour="sheet"
          onEscapeKeyDown={(e) => {
            // Esc raeumt immer nur die OBERSTE Schicht ab (B50).
            //
            // Die Treffer-Vorschau stand hier von Anfang an. Das
            // Scan-Fenster fehlte: Es hoert selbst auf Escape und schliesst
            // sich, aber Radix bekam denselben Tastendruck und nahm das
            // ganze Sheet mit — samt aller Eingaben. Gemessen im
            // Pruefstand: Esc bei offenem Scanner, danach war nicht nur die
            // Kamera weg, sondern auch das Formular.
            if (isbnReviewOffen.current || scanOffen) e.preventDefault();
          }}
        >
          <header className="flex h-14 shrink-0 items-center justify-between border-b border-line px-4">
            <Dialog.Title className="font-serif text-lg text-ink">Neue Bestellung</Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                className="inline-flex h-11 w-11 items-center justify-center rounded text-ink-soft hover:bg-cream-dark"
                aria-label="Schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </header>

          <div className="flex-1 space-y-5 overflow-y-auto p-4">
            {/* A9.1: Wer erfasst, steht dort, wo erfasst wird. */}
            <SchreibtAls text="Erfasst von" />

            {draft.angebot !== null && (
              <DraftHinweis
                text="eine begonnene Bestellung"
                onWiederherstellen={() => {
                  const daten = draft.annehmen();
                  if (daten !== null) dispatch({ type: 'RESTORE', state: daten });
                }}
                onVerwerfen={draft.verwerfen}
              />
            )}

            <section data-tour="sheet-bestellweg">
              <SektionTitel titel="Bestellweg" fehler={versucht ? fehler.bestellweg : undefined} />
              <BestellwegPicker
                value={state.bestellwegId}
                onChange={(id) => dispatch({ type: 'SET_BESTELLWEG', id })}
              />
            </section>

            {/* Die Weiche steht direkt unter dem Bestellweg: beides ist
                eine Frage, die am Anfang des Gespraechs beantwortet wird
                ("wie kam die Bestellung rein" / "wie geht sie raus"). */}
            <section data-tour="sheet-zustellweg">
              <SektionTitel
                titel="Abholung oder Versand"
                fehler={versucht ? fehler.zustellweg : undefined}
              />
              <ZustellwegPicker
                weg={state.zustellweg}
                versandartId={state.versandartId}
                onWeg={(weg) => dispatch({ type: 'SET_ZUSTELLWEG', weg })}
                onVersandart={(id) => dispatch({ type: 'SET_VERSANDART', id })}
              />
              {/* A25: Der Geschenkwunsch steht hier, weil an dieser Stelle
                  ohnehin ueber den Ausgang entschieden wird — und weil er
                  im Gespraech faellt, nicht am Packtisch. Bis hierher
                  ueberlebte er die Tage bis zum Packen nur, wenn ihn
                  jemand in die Notiz schrieb.

                  Nicht auf Versand beschraenkt: auch eine Abholung kann
                  ein Geschenk sein, das verpackt bereitliegen soll. */}
              <label className="mt-3 flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-md border border-line bg-cream px-3 py-2 hover:border-akzent">
                <input
                  type="checkbox"
                  checked={state.istGeschenk}
                  onChange={() => dispatch({ type: 'TOGGLE_GESCHENK' })}
                  className="h-5 w-5 shrink-0 accent-[color:var(--akzent)]"
                />
                <Gift aria-hidden className="h-4 w-4 shrink-0 text-gold-dark" />
                <span className="text-sm text-ink">Als Geschenk gewünscht</span>
              </label>
            </section>

            <section data-tour="sheet-kunde">
              <SektionTitel titel="Kunde" fehler={versucht ? fehler.kunde : undefined} />
              {state.neuerKunde ? (
                <NeuerKundeForm
                  value={state.neuerKunde}
                  onChange={(patch) => dispatch({ type: 'UPDATE_NEUER_KUNDE', patch })}
                  onCancel={() => dispatch({ type: 'CLEAR_NEUER_KUNDE' })}
                />
              ) : (
                <KundenSearch
                  selected={state.kundeRef}
                  onSelect={(k) => dispatch({ type: 'SET_KUNDE', kunde: k })}
                  onClear={() => dispatch({ type: 'CLEAR_KUNDE' })}
                  onStartNew={() => dispatch({ type: 'START_NEUER_KUNDE' })}
                />
              )}
            </section>

            <section>
              <RechnungsadresseSection
                abweichend={state.rechnungAbweichend}
                adresse={state.rechnungAdresse}
                onToggle={() => dispatch({ type: 'TOGGLE_RECHNUNG_ABWEICHEND' })}
                onChange={(patch) => dispatch({ type: 'UPDATE_RECHNUNG_ADRESSE', patch })}
              />
            </section>

            <section>
              <SektionTitel titel="Positionen" fehler={versucht ? fehler.positionen : undefined} />
              <div className="space-y-2">
                <IsbnInput
                  anker="sheet-isbn"
                  // E6: Ohne diese Zeile blieb der Scan-Knopf neben dem
                  // ISBN-Feld unsichtbar — IsbnInput zeigt ihn nur, wenn
                  // ein Aufrufer ihn auch bedienen kann. Ausgerechnet im
                  // Erfassungs-Formular, wo am ehesten gescannt wird.
                  onScannerOpen={() => setScanOffen(true)}
                  onAdd={(p) => dispatch({ type: 'ADD_POSITION', position: p })}
                  wert={state.entwurfIsbn}
                  onWertChange={(t) => dispatch({ type: 'SET_ENTWURF_ISBN', text: t })}
                  onReviewOffen={meldeReviewOffen}
                />
                <FreitextInput
                  anker="sheet-freitext"
                  onAdd={(p) => dispatch({ type: 'ADD_POSITION', position: p })}
                  wert={state.entwurfFreitext}
                  onWertChange={(t) => dispatch({ type: 'SET_ENTWURF_FREITEXT', text: t })}
                />
                {/* Anker auch fuer die leere Liste: die Tour wartet
                    darauf, dass hier eine Position auftaucht. */}
                {state.positionen.length === 0 ? (
                  <p
                    data-tour="sheet-positionen"
                    className="rounded-md border border-dashed border-line bg-cream-warm px-3 py-4 text-center text-sm text-ink-faint"
                  >
                    Noch keine Positionen.
                  </p>
                ) : (
                  <div className="space-y-2" data-tour="sheet-positionen">
                    {state.positionen.map((p) => (
                      <PositionCard
                        key={p._uid}
                        position={p}
                        onUpdate={(patch) =>
                          dispatch({ type: 'UPDATE_POSITION', uid: p._uid, patch })
                        }
                        onRemove={() => dispatch({ type: 'REMOVE_POSITION', uid: p._uid })}
                      />
                    ))}
                  </div>
                )}
              </div>
            </section>

            {/* A56 (#135): der Kundenwunsch — zurückhaltend, weil er
                selten vorkommt, aber nicht versteckt: Was gesetzt ist,
                steht im ZUGEKLAPPTEN Zustand daneben. Ein aufklappbarer
                Bereich, der verschweigt, dass in ihm etwas steht, ist
                eine Falle. */}
            <KundenwunschBereich
              lieferwunsch={state.lieferwunsch}
              nachrichtWunsch={state.nachrichtWunsch}
              onLieferwunsch={(wunsch) => dispatch({ type: 'SET_LIEFERWUNSCH', wunsch })}
              onNachrichtWunsch={() => dispatch({ type: 'TOGGLE_NACHRICHT_WUNSCH' })}
            />

            <section data-tour="sheet-abholdatum">
              <h3 className="mb-2 text-sm font-medium text-ink">Termin vereinbart am</h3>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="date"
                  value={state.abholdatum}
                  onChange={(e) => dispatch({ type: 'SET_ABHOLDATUM', datum: e.target.value })}
                  className={`min-h-[44px] rounded-md border border-line bg-cream px-3 py-2 text-sm ${FOKUS_RING}`}
                  aria-label="Termin vereinbart am"
                  // Faengt vertippte Jahreszahlen ab ("05.09.26" wird im
                  // Datumsfeld zum Jahr 0026). Serverseitiges Gegenstueck
                  // steht in istKalenderdatum().
                  min="2020-01-01"
                  max="2100-12-31"
                />
                {state.abholdatum !== '' && (
                  <button
                    type="button"
                    onClick={() => dispatch({ type: 'SET_ABHOLDATUM', datum: '' })}
                    className="min-h-[44px] px-2 text-sm text-ink-soft hover:text-akzent"
                  >
                    Kein Termin
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-ink-faint">
                Optional. Treibt die Dringlichkeit im Dashboard.
              </p>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-medium text-ink">Notiz</h3>
              <textarea
                value={state.notiz}
                onChange={(e) => dispatch({ type: 'SET_NOTIZ', text: e.target.value })}
                rows={3}
                className={`min-h-[44px] w-full rounded-md border border-line bg-cream px-3 py-2 text-sm ${FOKUS_RING}`}
                placeholder="Anmerkung zur Bestellung (optional)"
              />
            </section>

            {auth.data && (
              <section className="text-xs text-ink-faint">
                Erfasser:{' '}
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 rounded-full align-middle"
                  style={{ backgroundColor: auth.data.user.farbe ?? C.inkFaint }}
                />{' '}
                <span className="align-middle">{userLabel(auth.data.user)}</span>
              </section>
            )}
          </div>

          {/* Das Formular ist auf schmalen Geräten von Haus aus
              bildschirmfüllend (w-full über die ganze Höhe) — es musste
              dafür nichts umgebaut werden. Nur der Home-Indikator lag auf
              „Speichern": deshalb die Polsterung unten. */}
          <footer className="flex shrink-0 flex-wrap items-center justify-end gap-x-3 gap-y-2 border-t border-line bg-cream-warm px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
            {hatFehler && (
              <p className="mr-auto flex items-center gap-1.5 text-xs text-coral">
                <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                <span>
                  Noch offen:{' '}
                  {[fehler.bestellweg, fehler.zustellweg, fehler.kunde, fehler.positionen]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </p>
            )}
            <Button variant="ghost" onClick={onClose}>
              Abbrechen
            </Button>
            {/* Bewusst NICHT disabled bei Validierungsfehlern: ein Klick soll
                sagen, was fehlt, statt wirkungslos zu bleiben (B4). */}
            <Button
              variant="primary"
              data-tour="sheet-speichern"
              onClick={() => void speichern()}
              disabled={create.isPending}
            >
              {create.isPending ? 'Speichern …' : 'Bestellung speichern'}
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>

      {/* Liegt ausserhalb des Dialog-Inhalts, damit das Scan-Fenster den
          ganzen Bildschirm bekommt und nicht im Formular klemmt. */}
      {scanOffen && (
        <ScanOverlay
          onSchliessen={() => setScanOffen(false)}
          onCode={(roh) => {
            setScanOffen(false);
            // Derselbe Deuter wie im Dashboard: ein QR-Aufkleber von
            // fremder Hand darf hier so wenig etwas ausloesen wie dort.
            const deutung = scanDeuten(roh, scanHosts);
            if (deutung.art === 'fremd') return;
            dispatch({ type: 'SET_ENTWURF_ISBN', text: deutung.wert });
          }}
        />
      )}
    </Dialog.Root>
  );
}
